/**
 * Standard Error Types for BeCreatives Customer Frontend
 * 
 * This module defines all error categories, codes, and their associated
 * user-friendly messages. Used throughout the application for consistent
 * error handling and display.
 */

/**
 * Error categories for classification
 */
export enum ErrorCategory {
  NETWORK = 'network',
  SERVER = 'server',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  TIMEOUT = 'timeout',
  CLIENT = 'client',
  UNKNOWN = 'unknown',
}

/**
 * Error codes mapped to HTTP status codes and custom error types
 */
export enum ErrorCode {
  // Network errors
  NETWORK_OFFLINE = 'NETWORK_OFFLINE',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_ERROR = 'NETWORK_ERROR',
  
  // Server errors (5xx)
  SERVER_ERROR = 'SERVER_ERROR',
  SERVER_UNAVAILABLE = 'SERVER_UNAVAILABLE',
  SERVER_OVERLOADED = 'SERVER_OVERLOADED',
  
  // Authentication errors (401)
  AUTH_EXPIRED = 'AUTH_EXPIRED',
  AUTH_INVALID = 'AUTH_INVALID',
  AUTH_REQUIRED = 'AUTH_REQUIRED',
  
  // Authorization errors (403)
  FORBIDDEN = 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  
  // Client errors (4xx)
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CONFLICT = 'CONFLICT',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  
  // Generic
  UNKNOWN = 'UNKNOWN',
  CANCELLED = 'CANCELLED',
}

/**
 * Configuration for how errors should be handled
 */
export interface ErrorHandlingConfig {
  showToast: boolean;
  isRecoverable: boolean;
  shouldRedirect: boolean;
  redirectPath?: string;
  retryable: boolean;
  maxRetries: number;
  retryDelay: number; // milliseconds
}

/**
 * Error definition with all associated metadata
 */
export interface ErrorDefinition {
  code: ErrorCode;
  category: ErrorCategory;
  translationKey: string;
  defaultMessage: string;
  defaultTitle: string;
  handling: ErrorHandlingConfig;
}

/**
 * Standard error definitions with user-friendly messages and handling config
 */
export const ERROR_DEFINITIONS: Record<ErrorCode, ErrorDefinition> = {
  // Network errors
  [ErrorCode.NETWORK_OFFLINE]: {
    code: ErrorCode.NETWORK_OFFLINE,
    category: ErrorCategory.NETWORK,
    translationKey: 'errors:network.offline',
    defaultTitle: 'No Internet Connection',
    defaultMessage: 'Please check your internet connection and try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 3,
      retryDelay: 2000,
    },
  },
  [ErrorCode.NETWORK_TIMEOUT]: {
    code: ErrorCode.NETWORK_TIMEOUT,
    category: ErrorCategory.TIMEOUT,
    translationKey: 'errors:network.timeout',
    defaultTitle: 'Request Timed Out',
    defaultMessage: 'The request took too long. Please try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 2,
      retryDelay: 3000,
    },
  },
  [ErrorCode.NETWORK_ERROR]: {
    code: ErrorCode.NETWORK_ERROR,
    category: ErrorCategory.NETWORK,
    translationKey: 'errors:network.generic',
    defaultTitle: 'Connection Error',
    defaultMessage: 'Unable to connect to the server. Please try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 2,
      retryDelay: 2000,
    },
  },

  // Server errors
  [ErrorCode.SERVER_ERROR]: {
    code: ErrorCode.SERVER_ERROR,
    category: ErrorCategory.SERVER,
    translationKey: 'errors:server.generic',
    defaultTitle: 'Server Error',
    defaultMessage: 'Something went wrong on our end. Please try again later.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 1,
      retryDelay: 5000,
    },
  },
  [ErrorCode.SERVER_UNAVAILABLE]: {
    code: ErrorCode.SERVER_UNAVAILABLE,
    category: ErrorCategory.SERVER,
    translationKey: 'errors:server.unavailable',
    defaultTitle: 'Service Unavailable',
    defaultMessage: 'Our service is temporarily unavailable. Please try again in a few minutes.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 2,
      retryDelay: 10000,
    },
  },
  [ErrorCode.SERVER_OVERLOADED]: {
    code: ErrorCode.SERVER_OVERLOADED,
    category: ErrorCategory.SERVER,
    translationKey: 'errors:server.overloaded',
    defaultTitle: 'Server Busy',
    defaultMessage: 'Our servers are experiencing high traffic. Please try again shortly.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 2,
      retryDelay: 15000,
    },
  },

  // Authentication errors
  [ErrorCode.AUTH_EXPIRED]: {
    code: ErrorCode.AUTH_EXPIRED,
    category: ErrorCategory.AUTHENTICATION,
    translationKey: 'errors:auth.expired',
    defaultTitle: 'Session Expired',
    defaultMessage: 'Your session has expired. Please log in again.',
    handling: {
      showToast: true,
      isRecoverable: false,
      shouldRedirect: true,
      redirectPath: '/login',
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.AUTH_INVALID]: {
    code: ErrorCode.AUTH_INVALID,
    category: ErrorCategory.AUTHENTICATION,
    translationKey: 'errors:auth.invalid',
    defaultTitle: 'Authentication Failed',
    defaultMessage: 'Invalid credentials. Please check and try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.AUTH_REQUIRED]: {
    code: ErrorCode.AUTH_REQUIRED,
    category: ErrorCategory.AUTHENTICATION,
    translationKey: 'errors:auth.required',
    defaultTitle: 'Login Required',
    defaultMessage: 'Please log in to continue.',
    handling: {
      showToast: true,
      isRecoverable: false,
      shouldRedirect: true,
      redirectPath: '/login',
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },

  // Authorization errors
  [ErrorCode.FORBIDDEN]: {
    code: ErrorCode.FORBIDDEN,
    category: ErrorCategory.AUTHORIZATION,
    translationKey: 'errors:auth.forbidden',
    defaultTitle: 'Access Denied',
    defaultMessage: 'You don\'t have permission to access this resource.',
    handling: {
      showToast: true,
      isRecoverable: false,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.INSUFFICIENT_PERMISSIONS]: {
    code: ErrorCode.INSUFFICIENT_PERMISSIONS,
    category: ErrorCategory.AUTHORIZATION,
    translationKey: 'errors:auth.insufficient_permissions',
    defaultTitle: 'Insufficient Permissions',
    defaultMessage: 'You don\'t have the required permissions for this action.',
    handling: {
      showToast: true,
      isRecoverable: false,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },

  // Client errors
  [ErrorCode.NOT_FOUND]: {
    code: ErrorCode.NOT_FOUND,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:client.not_found',
    defaultTitle: 'Not Found',
    defaultMessage: 'The requested resource could not be found.',
    handling: {
      showToast: true,
      isRecoverable: false,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.BAD_REQUEST]: {
    code: ErrorCode.BAD_REQUEST,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:client.bad_request',
    defaultTitle: 'Invalid Request',
    defaultMessage: 'The request could not be processed. Please check your input.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.VALIDATION_ERROR]: {
    code: ErrorCode.VALIDATION_ERROR,
    category: ErrorCategory.VALIDATION,
    translationKey: 'errors:validation.generic',
    defaultTitle: 'Validation Error',
    defaultMessage: 'Please check your input and try again.',
    handling: {
      showToast: false, // Validation errors shown inline
      isRecoverable: true,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
  [ErrorCode.CONFLICT]: {
    code: ErrorCode.CONFLICT,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:client.conflict',
    defaultTitle: 'Conflict',
    defaultMessage: 'This action conflicts with the current state. Please refresh and try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 1,
      retryDelay: 1000,
    },
  },
  [ErrorCode.TOO_MANY_REQUESTS]: {
    code: ErrorCode.TOO_MANY_REQUESTS,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:client.rate_limited',
    defaultTitle: 'Too Many Requests',
    defaultMessage: 'You\'ve made too many requests. Please wait a moment before trying again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 1,
      retryDelay: 60000,
    },
  },
  [ErrorCode.PAYLOAD_TOO_LARGE]: {
    code: ErrorCode.PAYLOAD_TOO_LARGE,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:client.payload_too_large',
    defaultTitle: 'File Too Large',
    defaultMessage: 'The file you\'re trying to upload is too large.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },

  // Generic errors
  [ErrorCode.UNKNOWN]: {
    code: ErrorCode.UNKNOWN,
    category: ErrorCategory.UNKNOWN,
    translationKey: 'errors:unknown',
    defaultTitle: 'Something Went Wrong',
    defaultMessage: 'An unexpected error occurred. Please try again.',
    handling: {
      showToast: true,
      isRecoverable: true,
      shouldRedirect: false,
      retryable: true,
      maxRetries: 1,
      retryDelay: 2000,
    },
  },
  [ErrorCode.CANCELLED]: {
    code: ErrorCode.CANCELLED,
    category: ErrorCategory.CLIENT,
    translationKey: 'errors:cancelled',
    defaultTitle: 'Request Cancelled',
    defaultMessage: 'The request was cancelled.',
    handling: {
      showToast: false,
      isRecoverable: false,
      shouldRedirect: false,
      retryable: false,
      maxRetries: 0,
      retryDelay: 0,
    },
  },
};

/**
 * Map HTTP status codes to error codes
 */
export const HTTP_STATUS_TO_ERROR_CODE: Record<number, ErrorCode> = {
  0: ErrorCode.NETWORK_ERROR,
  400: ErrorCode.BAD_REQUEST,
  401: ErrorCode.AUTH_REQUIRED,
  403: ErrorCode.FORBIDDEN,
  404: ErrorCode.NOT_FOUND,
  408: ErrorCode.NETWORK_TIMEOUT,
  409: ErrorCode.CONFLICT,
  413: ErrorCode.PAYLOAD_TOO_LARGE,
  422: ErrorCode.VALIDATION_ERROR,
  429: ErrorCode.TOO_MANY_REQUESTS,
  500: ErrorCode.SERVER_ERROR,
  502: ErrorCode.SERVER_UNAVAILABLE,
  503: ErrorCode.SERVER_UNAVAILABLE,
  504: ErrorCode.SERVER_OVERLOADED,
};

/**
 * Get error definition by HTTP status code
 */
export function getErrorDefinitionByStatus(status: number): ErrorDefinition {
  const errorCode = HTTP_STATUS_TO_ERROR_CODE[status] || ErrorCode.UNKNOWN;
  return ERROR_DEFINITIONS[errorCode];
}

/**
 * Get error definition by error code
 */
export function getErrorDefinition(code: ErrorCode): ErrorDefinition {
  return ERROR_DEFINITIONS[code] || ERROR_DEFINITIONS[ErrorCode.UNKNOWN];
}

/**
 * Check if an error is recoverable
 */
export function isRecoverableError(code: ErrorCode): boolean {
  const definition = getErrorDefinition(code);
  return definition.handling.isRecoverable;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(code: ErrorCode): boolean {
  const definition = getErrorDefinition(code);
  return definition.handling.retryable;
}
